import type { InfiniteData } from '@tanstack/react-query';
import type { PaginatedResponse } from '@/lib/api';

/**
 * Flattens the pages of an infinite query into one row list.
 *
 * @since 1.3.0
 * @param data - the infinite query data, or undefined while nothing has loaded
 * @returns every row across every loaded page, in page order
 */
export function flattenRows<TRow>(data: InfiniteData<PaginatedResponse<TRow>> | undefined): TRow[] {
    return data?.pages.flatMap((page) => page.rows) ?? [];
}

/**
 * Counts the rows loaded so far, marking the count as a floor while the list has more pages
 * to fetch, so a heading over a paged table never reads as a total it has not counted.
 *
 * @since 1.4.0
 * @param rows - the rows loaded so far
 * @param hasMore - whether the list has another page to fetch
 * @returns the count to show
 */
export function countLoaded(rows: readonly unknown[], hasMore: boolean): string {
    return hasMore ? `${rows.length}+` : String(rows.length);
}
